import { useState } from 'react'
import { Layout as LayoutIcon, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ThumbnailPreviewProps {
  src?: string | null
  alt: string
  /** Picks the placeholder icon when there's no usable image. */
  kind: 'layout' | 'location'
  /** Sizing lives with the caller — e.g. "h-10 w-16" in a row, "h-28 w-full" on a card. */
  className?: string
  iconClassName?: string
  /** Fired once if the image 404s — lets a caller drop a large hero instead of showing an empty box. */
  onLoadError?: () => void
}

/**
 * Layout/location preview image with a graceful placeholder.
 *
 * Thumbnails are user content on Supabase Storage and can 404 (deleted bucket
 * object, half-finished capture), so a load error falls back to the same
 * placeholder as a missing URL rather than leaving a broken-image glyph.
 */
export function ThumbnailPreview({
  src,
  alt,
  kind,
  className,
  iconClassName = 'h-4 w-4',
  onLoadError,
}: ThumbnailPreviewProps) {
  const [failed, setFailed] = useState(false)
  const Icon = kind === 'location' ? MapPin : LayoutIcon

  if (!src || failed) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded bg-muted text-muted-foreground/40',
          className,
        )}
        aria-label={`${alt} (no preview)`}
      >
        <Icon className={iconClassName} />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => {
        setFailed(true)
        onLoadError?.()
      }}
      className={cn('shrink-0 rounded bg-muted object-cover', className)}
    />
  )
}
